import { ChevronDown, Loader2, Split, Tag, Trash2, TriangleAlert, X } from "lucide-react";
import { DeviceMenu, type DeviceMenuPick } from "@/components/device-menu";
import type { EditorDevice } from "./model";

/**
 * The floating bar that appears once lines are ticked (Faster Estimates):
 * "N selected · Tag N lines to device ▾ · Split · Delete · ✕". One tag moves
 * every ticked line; the app's own lines reword for the device.
 */
export function SelectionBar({
    count,
    total,
    onSelectAll,
    devices,
    onTag,
    tagOpen,
    onTagOpenChange,
    onSplit,
    onDelete,
    pending,
    onClear,
}: {
    count: number;
    total: number;
    onSelectAll: () => void;
    devices: readonly EditorDevice[];
    onTag: (pick: DeviceMenuPick) => void;
    tagOpen: boolean;
    onTagOpenChange: (open: boolean) => void;
    /** Present when a ticked line is a shared fee that can split. */
    onSplit: (() => void) | null;
    onDelete: () => void;
    pending: boolean;
    onClear: () => void;
}) {
    if (count === 0) return null;
    const noun = `${count} line${count === 1 ? "" : "s"}`;
    return (
        <div className="pointer-events-none sticky bottom-3 z-20 mt-2 flex justify-center">
            <div
                role="toolbar"
                aria-label="Selected lines"
                data-testid="selection-bar"
                className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1 rounded-[14px] bg-shell py-1.5 pl-3.5 pr-1.5 text-[13px] text-shell-foreground shadow-elevated"
            >
                <span className="mr-1.5 inline-flex items-center gap-2 font-bold">
                    <i className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-primary px-1.5 text-xs not-italic text-primary-foreground">
                        {count}
                    </i>
                    selected
                    {count < total && (
                        <button type="button" className="text-xs font-medium underline" onClick={onSelectAll}>
                            all {total}
                        </button>
                    )}
                </span>
                {devices.length > 0 ? (
                    <DeviceMenu
                        devices={devices}
                        mode="tag"
                        side="top"
                        open={tagOpen}
                        onOpenChange={onTagOpenChange}
                        onPick={onTag}
                        trigger={
                            <button
                                type="button"
                                disabled={pending}
                                className="inline-flex items-center gap-1.5 rounded-[9px] bg-primary px-2.5 py-[7px] font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-60"
                            >
                                <Tag className="h-3.5 w-3.5" />
                                Tag {noun} to device
                                <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                        }
                    />
                ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 text-xs opacity-80">
                        <TriangleAlert className="h-3.5 w-3.5" /> No devices at this location yet
                    </span>
                )}
                {onSplit && (
                    <button
                        type="button"
                        disabled={pending}
                        className="inline-flex items-center gap-1.5 rounded-[9px] px-2.5 py-[7px] font-semibold hover:bg-shell-foreground/10"
                        onClick={onSplit}
                    >
                        <Split className="h-3.5 w-3.5" />
                        Split
                    </button>
                )}
                <button
                    type="button"
                    disabled={pending}
                    className="inline-flex items-center gap-1.5 rounded-[9px] px-2.5 py-[7px] font-semibold hover:bg-shell-foreground/10"
                    onClick={onDelete}
                >
                    {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Delete
                </button>
                <span className="mx-0.5 h-5 w-px bg-shell-foreground/20" />
                <button
                    type="button"
                    title="Clear selection"
                    aria-label="Clear selection"
                    className="inline-flex items-center rounded-[9px] px-2.5 py-[7px] hover:bg-shell-foreground/10"
                    onClick={onClear}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

/**
 * Decision 3: lines with no device warn at send, never block.
 * "N lines have no device · Tag them · Send anyway". "Whole job" lines don't count.
 */
export function SendGuard({
    count,
    onTagThem,
    onSendAnyway,
    onDismiss,
}: {
    count: number;
    onTagThem: () => void;
    onSendAnyway: () => void;
    onDismiss: () => void;
}) {
    return (
        <div
            role="alert"
            data-testid="send-guard"
            className="flex flex-wrap items-center gap-2 rounded-full bg-warning/15 py-1.5 pl-3 pr-1.5 text-[12.5px] font-semibold text-warning"
        >
            <TriangleAlert className="h-3.5 w-3.5" />
            {count} line{count === 1 ? " has" : "s have"} no device
            <button
                type="button"
                className="rounded-full bg-card px-2.5 py-1 text-[11.5px] font-bold text-warning"
                onClick={onTagThem}
            >
                Tag them
            </button>
            <button
                type="button"
                className="rounded-full bg-card px-2.5 py-1 text-[11.5px] font-bold text-warning"
                onClick={onSendAnyway}
            >
                Send anyway
            </button>
            <button
                type="button"
                aria-label="Dismiss"
                className="rounded-full px-1.5 py-1 text-warning/80 hover:text-warning"
                onClick={onDismiss}
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
